import React from 'react';
import invariant from 'tiny-invariant';

import { CurrentDesignCard } from 'src/modules/designStudio/components/CurrentDesignCard';
import type { CardCopyType } from 'src/modules/designStudio/components/Toolbar/toolBarConstants';
import {
  CardTypes,
  cardCopy,
} from 'src/modules/designStudio/components/Toolbar/toolBarConstants';
import { FeatureFlag } from 'src/shared/constants';
import { useTranslate, useTreatment } from 'src/shared/hooks';
import { TranslationNamespace } from 'src/shared/hooks/useTranslate';

import type { Menu } from '../../constants';
import type { ExtendedMacheteViewer } from '../../helpers/machete';

import { DesignCard } from './DesignCard';
import { InactiveFeatureCard } from './InactiveFeatureCard';
import { ResetCard } from './ResetCard';
import { ShareCard } from './ShareCard';

interface PropTypes {
  menu: Menu | null;
  jobId?: number;
  closeMenu?: () => void;
  viewer: ExtendedMacheteViewer | null;
  requestingOrgName?: string;
  setCardHeight?: (currentHeight: number) => void;
  userId?: number;
  toolbarRef: React.RefObject<HTMLElement> | null;
}

const DesignMenu = ({
  menu,
  closeMenu,
  jobId,
  viewer,
  requestingOrgName,
  setCardHeight,
  userId,
  toolbarRef,
}: PropTypes) => {
  let itemCopy: CardCopyType = cardCopy.DEFAULT;
  const t = useTranslate(TranslationNamespace.DESIGN_STUDIO);
  const doorGroupsIsEnabled =
    useTreatment(FeatureFlag.DesignStudio.DS_DOOR_GROUPS) === 'on';

  /**
   * TODO: [https://hoverinc.atlassian.net/browse/TDP-5619] Set DOOR.cardType = DESIGN when feature flag for doors is removed
   */
  if (cardCopy && cardCopy.DOOR) {
    cardCopy.DOOR.cardType = doorGroupsIsEnabled
      ? CardTypes.DESIGN
      : CardTypes.INACTIVE_FEATURE;
  }

  if (menu) {
    itemCopy = cardCopy?.[menu];
  }

  const body = itemCopy.body ? (t(itemCopy.body) as string) : '';
  const title = itemCopy.title ? (t(itemCopy.title) as string) : '';

  switch (itemCopy.cardType) {
    case CardTypes.INACTIVE_FEATURE:
      if (!closeMenu) {
        return null;
      }
      return (
        <InactiveFeatureCard
          bodyText={body}
          closeMenu={closeMenu}
          feature={itemCopy.feature}
          headerText={title}
          menu={menu}
          toolbarRef={toolbarRef}
        />
      );
    case CardTypes.RESET:
      if (!closeMenu) {
        return null;
      }
      return (
        <ResetCard
          bodyText={body}
          closeMenu={closeMenu}
          headerText={title}
          menu={menu}
          toolbarRef={toolbarRef}
        />
      );
    case CardTypes.SHARE:
      invariant(jobId, 'Cannot share without jobId!');
      if (!closeMenu) {
        return null;
      }
      return (
        <ShareCard
          closeMenu={closeMenu}
          headerText={title}
          jobId={jobId}
          menu={menu}
          toolbarRef={toolbarRef}
          userId={userId}
          viewer={viewer}
        />
      );
    case CardTypes.SHOW_DESIGN:
      if (!setCardHeight) {
        return null;
      }
      return (
        <CurrentDesignCard
          menu={menu}
          onHeightChange={setCardHeight}
          toolbarRef={toolbarRef}
        />
      );
    default:
      if (!closeMenu) {
        return null;
      }
      return (
        <DesignCard
          closeMenu={closeMenu}
          headerText={title}
          menu={menu}
          requestingOrgName={requestingOrgName}
          toolbarRef={toolbarRef}
        />
      );
  }
};

export { DesignMenu };
